import React, { useMemo, useState, useEffect } from "react";

// Material-UI and custom components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useTranslation } from "react-i18next";

// Context and Icons
import { useUserContext } from "context/UserContext";
import getGraphQLEndpoint from "utils/getGraphQLEndpoint";
import { isBkLikeRole } from "utils/roleUtils";

function WarehousesBK() {
  // =================================================================
  // SECTION 1: Original logic for iframe display
  // =================================================================
  const { userData, role, idToken, posPidArray } = useUserContext();
  const { t } = useTranslation("common");
  const backendUrl = useMemo(getGraphQLEndpoint, []);
  const [metabaseUrl, setMetabaseUrl] = useState("");
  const [error, setError] = useState(null);
  const appsmithUrl = `https://app.appsmith.com/app/warehouse/page1-68910adecfca0f3d24e667d2?email=${encodeURIComponent(
    userData?.email ?? ""
  )}&embed=true&hide_navigation_bar=true`;

  console.log(appsmithUrl);

  useEffect(() => {
    const fetchMetabaseUrl = async () => {
      if (role !== "SP") return;
      if (!idToken) {
        setError("User is not authenticated.");
        return;
      }

      try {
        const pids = Array.isArray(posPidArray)
          ? posPidArray
              .map((item) => (item?.pos_pid ? String(item.pos_pid).trim() : ""))
              .filter(Boolean)
          : [];
        const response = await fetch(backendUrl, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            query: `
              query GetMetabaseURL($pids: [String!]!) {
                GetMetabaseEmbedURLwithPIDforTransaction(pids: $pids)
              }
            `,
            variables: { pids },
          }),
        });

        if (!response.ok) throw new Error("Failed to fetch Metabase URL");
        const data = await response.json();
        setMetabaseUrl(data.data.GetMetabaseEmbedURLwithPIDforTransaction);
      } catch (err) {
        console.error(err);
        setError("Failed to load Metabase transaction URL");
      }
    };

    fetchMetabaseUrl();
  }, [role, idToken, posPidArray, backendUrl]);

  // =================================================================
  // SECTION 3: JSX Rendering
  // =================================================================

  const renderContent = () => {
    if (role === "SP") {
      return metabaseUrl ? (
        <iframe
          src={metabaseUrl}
          width="100%"
          height="800px"
          frameBorder="0"
          title="Metabase Transactions Embed"
        />
      ) : (
        <MDTypography p={2}>{error || "Loading Metabase Transactions..."}</MDTypography>
      );
    }

    if (isBkLikeRole(role) || role === "CM") {
      return (
        <iframe
          key={userData?.email}
          src={appsmithUrl}
          width="100%"
          height="650px"
          frameBorder="0"
          title="Appsmith Transactions Embed"
        />
      );
    }

    return <MDTypography p={2}>Loading content...</MDTypography>;
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                sx={{
                  px: 3,
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: 2,
                }}
              >
                <MDTypography variant="h6" color="white">
                  {role === "SP" ? t("pageTitles.warehouseSp") : t("pageTitles.warehouseBk")}
                </MDTypography>
              </MDBox>

              <MDBox pt={3} px={0} width="100%">
                {renderContent()}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Dialog for the "Create Replenishment" functionality */}
    </DashboardLayout>
  );
}

export default WarehousesBK;
